import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import { RecurringInventoryTable } from './Components/RecurringInventoryTable';
import { PropertySelector } from './Components/PropertySelector';
import { DeleteRecurringInventoryItemConfirmationModal } from './Components/DeleteRecurringInventoryItemModal';
import { getPropertyList, getRequestTypes } from '../utils/api';
import { deleteRecurringInventoryItem } from './Components/FormikHelpers/Api';
import { setLoading } from '../../../store/actions';

const RecurringInventory = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [requestTypeOptions, setRequestTypeOptions] = useState([]);
  const [user, setUser] = useState(null);
  const [buildingType, setBuildingType] = useState('Apartment');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const [filters, setFilters] = useState({
    property_id: '',
    building_name: '',
    startDate: '',
    endDate: '',
    type: '',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch {
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchRequestTypes = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    try {
      const { requestType } = await getRequestTypes(currentUser?.company_id || currentUser?.id || '');
      setRequestTypeOptions(
        (requestType || []).map((t) => ({
          value: t._id,
          label: t.request_type || t.name || t.label || 'Unknown',
        }))
      );
    } catch {
      console.error('Failed to fetch request types');
    }
  }, [getUserFromToken]);

  const handleDeleteRequest = useCallback((item) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedItem?._id) return;
    try {
      dispatch(setLoading(true));
      await deleteRecurringInventoryItem(selectedItem._id);
      Swal.fire('Deleted', 'Inventory item deleted successfully', 'success');
      setTableRefreshKey((k) => k + 1);
    } catch {
      Swal.fire('Error', 'Failed to delete inventory item', 'error');
    } finally {
      dispatch(setLoading(false));
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    }
  }, [dispatch, selectedItem]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchRequestTypes(); }, [fetchRequestTypes]);

  return (
    <div className="page-content">
      <MetaTags><title>Rentdigicare | Recurring Inventory</title></MetaTags>
      <div className="container-fluid mb-2">
        <Breadcrumbs title="Inventory" breadcrumbItem="Recurring Inventory" />

        <PropertySelector
          propertyList={properties}
          selectedFilters={filters}
          onPropertySelectionChange={setFilters}
          onBuildingTypeChange={setBuildingType}
          user={user}
        />

        <RecurringInventoryTable
          key={tableRefreshKey}
          propertyId={filters.property_id}
          buildingName={filters.building_name}
          requestTypeOptions={requestTypeOptions}
          propertyList={properties}
          userRole={user?.role || 'other'}
          onDelete={handleDeleteRequest}
        />

        <DeleteRecurringInventoryItemConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setSelectedItem(null); }}
          onDelete={handleDeleteConfirm}
          item={selectedItem}
        />
      </div>
    </div>
  );
};

export { RecurringInventory };